import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PublicLeadForm from "../components/PublicLeadForm";
import useDocumentMeta from "../hooks/useDocumentMeta";
import "../components/CommitmentBanner.css";
import { Network, HeartHandshake, ShieldCheck, Database, ArrowDownRight } from "lucide-react";

export default function Alliances() {
  useDocumentMeta({
    title:       "Non-Profit Alliance Network · Coordinated Care | The Exchange Alliance",
    description: "Join the Exchange Alliance non-profit network. Regional housing, mental health, substance support, and food-security agencies connect through a secure bridge for consent-based, coordinated client care.",
    keywords:    "non-profit alliance network, coordinated care, regional non-profit partnership, alliance recruitment, PACT partner, consent-based care coordination",
    canonical:   "https://theexchangealliance.com/alliances",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="alliances-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-24 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3 flex items-center gap-2">
            <Network className="h-3 w-3"/> Alliance Recruitment
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0612] leading-[1.05]">
            One client.<br/>
            <span className="text-[#6B6F76]">Many agencies.</span><br/>
            One coordinated record.
          </h1>
          <p className="max-w-2xl text-base sm:text-lg text-[#6B6F76] mt-6 leading-relaxed">
            Regional non-profits joining the Exchange Alliance share one mandate — serving the Last,
            the Least and the Lost — along with the resources, coordinated care, and shared client
            record to carry it out without duplicating intake, paperwork, or case notes.
            One credentialed seat in our Personal Advancement and Career Training unifies your team with ours.
          </p>
        </div>
      </section>

      {/* 🤝 Alliance Recruitment narrative banner — the master visual story
          for non-profit recruitment. Artwork headline mirrors the hero
          ("One client. Many agencies. One coordinated record.") so the
          visual lands as a confirmation of the text intro above. Reuses
          the `commitment` CSS shell from the home page for visual unity. */}
      <section className="commitment" data-testid="alliance-recruitment-banner" aria-label="Alliance Recruitment · One client. Many agencies. One coordinated record.">
        <div className="commitment__shell">
          <div className="commitment__eyebrow">Why Member Agencies Join</div>
          <p className="commitment__caption">
            Stop running parallel intakes. Stop the redundant phone calls. One credentialed seat in the
            Personal Advancement and Career Training connects your agency to the partners a client has chosen to
            work with — with role-based access, encrypted documents, and sharing the participant controls.
          </p>

          <div className="commitment__frame">
            <img
              src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Real photograph: case managers, housing specialists, and outreach workers from multiple non-profit agencies stacking hands together over a shared case file, laptop, and coordination notes — one client, many independent partners, coordinating with the client's permission."
              className="commitment__img"
              loading="lazy"
              decoding="async"
              width="1600"
              height="1067"
            />
          </div>

          <div className="commitment__pillars" aria-hidden="false">
            <span>Shared Intake</span>
            <span>Coordinated Care</span>
            <span>No Duplicate Paperwork</span>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 sm:py-28 border-b border-[#E8E5DA] bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">How the Alliance Operates</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] mb-12 max-w-2xl">Three protocols that hold us accountable to every client we touch.</h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {PILLARS.map((p) => (
              <article key={p.title} data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g,'-')}`} className="bg-[#FAFAF7] border border-[#E8E5DA] p-7 sm:p-8" style={{ borderTop: `3px solid ${p.accent}` }}>
                <p.icon className="h-5 w-5 mb-4" style={{ color: p.accent }} strokeWidth={1.6}/>
                <h3 className="font-display font-bold text-xl tracking-tight text-[#0A0612] mb-2">{p.title}</h3>
                <p className="text-sm text-[#6B6F76] leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Value bullets */}
      <section className="py-20 sm:py-28 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">Why Member Agencies Join</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] leading-tight">A trusted seat at the table — not a vendor relationship.</h2>
            <p className="mt-5 text-[#6B6F76] text-base leading-relaxed">
              The Alliance is not a referral list. Member non-profits stay independent and connect through
              a secure bridge with role-based access, encrypted documents, and financial reconciliation —
              so, when a client chooses to share, coordination happens the day they walk in, not three weeks later.
            </p>
          </div>
          <ul className="space-y-5">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-4 group">
                <span className="font-mono text-[10px] tracking-[0.22em] text-[#C9A227] mt-1 shrink-0">{String(i+1).padStart(2,'0')}</span>
                <div>
                  <h4 className="font-display font-semibold text-lg text-[#0A0612]">{b.title}</h4>
                  <p className="text-sm text-[#6B6F76] mt-1 leading-relaxed">{b.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Intake form */}
      <section id="apply" className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3 inline-flex items-center gap-2">
              <ArrowDownRight className="h-3 w-3"/> Step 1 · Public Application
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612]">Become an Alliance Member</h2>
          </div>
          <PublicLeadForm
            kind="alliance_partnership"
            title="Apply for Alliance Partnership"
            subtitle="The CAO reviews every application personally. We'll respond within 2 business days with next-step credentials."
            accent="#C9A227"
          />
        </div>
      </section>

      <Footer />
    </main>
  );
}

const PILLARS = [
  { icon: HeartHandshake, accent: "#1B4332", title: "Coordinated Care",
    body: "Participants can choose to link their existing care records across The Life Foundation and the member agencies they pick — fewer duplicate intakes, no retelling their story at every stop." },
  { icon: ShieldCheck, accent: "#C9A227", title: "HIPAA-Grade Access",
    body: "Role-based access matrices keep clinical notes, financial details, and identity records visible only to the staff credentialed for each scope. Audited per access." },
  { icon: Database, accent: "#1E40AF", title: "Shared Resource Pool",
    body: "Bed availability, vocational tracks, and shared overhead stay current across partners. Client details move between agencies only when the client has chosen to share them." },
];

const BENEFITS = [
  { title: "Skip redundant case-management infrastructure", body: "Plug into a proven platform instead of building your own EMR. No license fees for Alliance members in good standing." },
  { title: "Coordinated weekly spending logs", body: "Joint program dollars reconcile in one ledger, so partners skip the paperwork. Audit-ready for every funder, every quarter." },
  { title: "Cross-agency resident phases", body: "When your client graduates Phase II and has chosen to share it, the partners they work with see it — no email chain, no portal hopping." },
  { title: "Built-in expense allocation", body: "Joint costs split fractionally across member agencies via the live Clearinghouse. No reconciliation meetings." },
];
